/**
 * Mailing List Manager
 * Manages voice-based selection for generating email lists from ensemble members
 */

import $ from 'jquery';

export default class MailingListManager {
    constructor() {
        this.voiceSelect = document.getElementById('voiceSelect');
        this.memberSelect = document.getElementById('memberSelect');
        this.emailList = document.getElementById('emailList');
        this.selectAllVoicesBtn = document.getElementById('selectAllVoices');
        this.clearVoicesBtn = document.getElementById('clearVoices');
        this.selectAllMembersBtn = document.getElementById('selectAllMembers');
        this.clearMembersBtn = document.getElementById('clearMembers');
        this.copyEmailsBtn = document.getElementById('copyEmails');
        this.copyStatus = document.getElementById('copyStatus');
        
        // Inactive members elements
        this.copyInactiveEmailsBtn = document.getElementById('copyInactiveEmails');
        this.copyInactiveStatus = document.getElementById('copyInactiveStatus');
        
        // Track previously selected voices to handle incremental changes
        this.previouslySelectedVoices = new Set();
        
        this.init();
    }
    
    init() {
        // Initialize Select2 for multiple selection
        if (this.voiceSelect) {
            $(this.voiceSelect).select2({
                placeholder: "Wybierz głosy...",
                allowClear: true,
                width: '100%'
            });
        }
        
        if (this.memberSelect) {
            $(this.memberSelect).select2({
                placeholder: "Wybierz członków...",
                allowClear: true,
                width: '100%'
            });
        }
        
        // Active members modal functionality
        if (this.voiceSelect) {
            this.setupVoiceSelection();
        }
        
        if (this.memberSelect) {
            this.setupMemberSelection();
            this.setupEmailGeneration();
        }
        
        // Inactive members modal functionality
        if (this.copyInactiveEmailsBtn) {
            this.setupInactiveCopyButton();
        }
    }
    
    setupVoiceSelection() {
        // Voice selection affects member selection - use Select2 event
        $(this.voiceSelect).on('change', () => {
            this.updateMemberSelectionByVoice();
        });
        
        // Select all voices button (only if button exists)
        if (this.selectAllVoicesBtn) {
            this.selectAllVoicesBtn.addEventListener('click', () => {
                // Get all voice IDs
                const allVoiceIds = Array.from(this.voiceSelect.options).map(option => option.value);
                
                // Select all in Select2
                $(this.voiceSelect).val(allVoiceIds).trigger('change');
            });
        }
        
        // Clear voices button (only if button exists)
        if (this.clearVoicesBtn) {
            this.clearVoicesBtn.addEventListener('click', () => {
                $(this.voiceSelect).val([]).trigger('change');
            });
        }
    }
    
    setupMemberSelection() {
        // Member selection affects email list - use Select2 event
        $(this.memberSelect).on('change', () => {
            this.updateEmailList();
        });
        
        // Select all members button
        this.selectAllMembersBtn.addEventListener('click', () => {
            const allMemberIds = Array.from(this.memberSelect.options).map(option => option.value);
            $(this.memberSelect).val(allMemberIds).trigger('change');
        });
        
        // Clear members button
        this.clearMembersBtn.addEventListener('click', () => {
            $(this.memberSelect).val([]).trigger('change');
        });
    }
    
    setupEmailGeneration() {
        // Copy button functionality
        this.copyEmailsBtn.addEventListener('click', () => {
            this.copyToClipboard(this.emailList.value, this.copyStatus);
        });
    }
    
    setupInactiveCopyButton() {
        this.copyInactiveEmailsBtn.addEventListener('click', () => {
            const inactiveEmailList = document.getElementById('inactiveEmailList');
            this.copyToClipboard(inactiveEmailList.value, this.copyInactiveStatus);
        });
    }
    
    updateMemberSelectionByVoice() {
        // If no voice select element exists, do nothing
        if (!this.voiceSelect) {
            return;
        }
        
        // Get currently selected voices from Select2
        const selectedVoiceIds = new Set($(this.voiceSelect).val() || []);
        
        // Find newly selected voices (added)
        const newlySelected = new Set([...selectedVoiceIds].filter(x => !this.previouslySelectedVoices.has(x)));
        
        // Find deselected voices (removed)
        const deselected = new Set([...this.previouslySelectedVoices].filter(x => !selectedVoiceIds.has(x)));
        
        // Get current member selection
        const currentMemberSelection = new Set($(this.memberSelect).val() || []);
        
        // Add members from newly selected voices
        newlySelected.forEach(voiceId => {
            Array.from(this.memberSelect.options).forEach(option => {
                const memberVoiceId = option.getAttribute('data-voice');
                if (memberVoiceId && memberVoiceId === voiceId) {
                    currentMemberSelection.add(option.value);
                }
            });
        });
        
        // Remove members from deselected voices
        deselected.forEach(voiceId => {
            Array.from(this.memberSelect.options).forEach(option => {
                const memberVoiceId = option.getAttribute('data-voice');
                if (memberVoiceId && memberVoiceId === voiceId) {
                    currentMemberSelection.delete(option.value);
                }
            });
        });
        
        // Update member select with new selection
        $(this.memberSelect).val([...currentMemberSelection]).trigger('change');
        
        // Update the tracking set
        this.previouslySelectedVoices = new Set(selectedVoiceIds);
    }
    
    updateEmailList() {
        // Get selected member IDs from Select2
        const selectedMemberIds = $(this.memberSelect).val() || [];
        
        // Get emails from selected options
        const selectedEmails = selectedMemberIds.map(memberId => {
            const option = this.memberSelect.querySelector(`option[value="${memberId}"]`);
            return option ? option.getAttribute('data-email') : null;
        }).filter(email => email); // Filter out empty emails
        
        // Generate comma-separated email list
        this.emailList.value = selectedEmails.join(', ');
    }
    
    async copyToClipboard(text, statusElement) {
        try {
            await navigator.clipboard.writeText(text);
            statusElement.style.display = 'inline';
            setTimeout(() => {
                statusElement.style.display = 'none';
            }, 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
            // Fallback for older browsers
            this.fallbackCopyToClipboard(text, statusElement);
        }
    }
    
    fallbackCopyToClipboard(text, statusElement) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            statusElement.style.display = 'inline';
            setTimeout(() => {
                statusElement.style.display = 'none';
            }, 2000);
        } catch (err) {
            console.error('Fallback: Failed to copy');
        }
        document.body.removeChild(textArea);
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    // Only initialize if we're on the users page and have the modals
    if (document.getElementById('activeMailingListModal') || document.getElementById('inactiveMailingListModal')) {
        new MailingListManager();
    }
});